import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { LockIcon, Loader2Icon } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

import config from "../config";
import { quotaDismissed } from "../redux/features/quotaActions";

const SALES_EMAIL = "sales@betterfield.pt";
const TIER_BADGE_CLASS = {
  basic: "bg-muted text-muted-foreground",
  pro: "bg-primary text-primary-foreground",
  enterprise: "bg-purple-600 text-white",
};

export default function UpgradeDialog() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { open, featureId, cost, balance } = useSelector(
    (state) => state.quota,
  );
  // Read the auth token explicitly from redux so these requests don't silently
  // rely on axios.defaults.headers being set by the main API middleware.
  const token = useSelector(
    (state) => state.session && state.session.current && state.session.current.token,
  );

  const [loading, setLoading] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [nextPlan, setNextPlan] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const handleClose = useCallback(() => {
    dispatch(quotaDismissed());
  }, [dispatch]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    const authConfig = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : undefined;
    Promise.all([
      axios.get(`${config.apiBaseUrl}/usage/subscription`, authConfig),
      axios.get(`${config.apiBaseUrl}/usage/plans`, authConfig),
    ])
      .then(([subRes, plansRes]) => {
        if (cancelled) return;
        const sub = subRes.data;
        const plans = plansRes.data || [];
        const currentIdx = plans.findIndex((p) => p.planId === sub.planId);
        setCurrentPlan(currentIdx >= 0 ? plans[currentIdx] : sub);
        setNextPlan(
          currentIdx >= 0 && currentIdx < plans.length - 1
            ? plans[currentIdx + 1]
            : null,
        );
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message || "Failed to load plans");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, token]);

  // Integer credits are our canonical unit, but defensively clamp tiny
  // negative values (left over from legacy fractional data) to 0 so the UI
  // never shows things like "-2.1e-15 credits".
  const clampCredits = (value) => {
    if (value == null) return null;
    const n = Math.round(Number(value));
    return Number.isFinite(n) ? Math.max(0, n) : null;
  };
  const displayCost = clampCredits(cost);
  const displayBalance = clampCredits(balance);

  const subject = encodeURIComponent(
    t("credits.upgradeDialog.mailSubject", {
      plan: nextPlan ? nextPlan.name : "",
    }),
  );
  const body = encodeURIComponent(
    t("credits.upgradeDialog.mailBody", {
      currentPlan: currentPlan ? currentPlan.name : "—",
      nextPlan: nextPlan ? nextPlan.name : "—",
    }),
  );
  const mailto = `mailto:${SALES_EMAIL}?subject=${subject}&body=${body}`;

  const renderPlanCard = (plan, isCurrent) => {
    if (!plan) return null;
    return (
      <Card
        className={cn(
          "min-w-[220px] flex-1",
          isCurrent && "border-primary/40 bg-muted/40",
        )}
      >
        <CardContent className="space-y-2 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold">{plan.name}</h3>
            {plan.tier ? (
              <Badge
                className={cn("text-xs", TIER_BADGE_CLASS[plan.tier] ?? "")}
              >
                {t(`credits.tier.${plan.tier}`, { defaultValue: plan.tier })}
              </Badge>
            ) : null}
            {isCurrent ? (
              <Badge variant="outline" className="text-xs">
                {t("credits.upgradeDialog.currentPlanChip")}
              </Badge>
            ) : null}
          </div>
          <p className="text-sm text-muted-foreground">
            {plan.priceEur != null
              ? t("credits.pricePerMonth", { price: plan.priceEur })
              : t("credits.priceCustom")}
          </p>
          <p className="text-sm">
            {t("credits.upgradeDialog.totalCreditsLabel", {
              count: plan.totalCredits,
            })}
          </p>
          {plan.unlockedFeatures && plan.unlockedFeatures.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {plan.unlockedFeatures.map((f) => (
                <Badge key={f} variant="outline" className="text-xs">
                  {t(`credits.unlocked.${f}`, { defaultValue: f })}
                </Badge>
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LockIcon className="size-4 text-amber-500" />
            {t("credits.upgradeDialog.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Alert variant="warning">
            <AlertDescription>
              {featureId
                ? t("credits.upgradeDialog.bodyWithFeature", {
                    feature: t(`credits.features.${featureId}`, {
                      defaultValue: featureId,
                    }),
                    cost: displayCost != null ? displayCost : "—",
                    balance: displayBalance != null ? displayBalance : "—",
                  })
                : t("credits.upgradeDialog.bodyGeneric")}
            </AlertDescription>
          </Alert>

          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2Icon className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : null}

          {loadError ? (
            <Alert variant="destructive">
              <AlertDescription>{loadError}</AlertDescription>
            </Alert>
          ) : null}

          {!loading && !loadError ? (
            <div className="flex flex-col gap-3 md:flex-row">
              {renderPlanCard(currentPlan, true)}
              {nextPlan ? (
                renderPlanCard(nextPlan, false)
              ) : (
                <div className="flex flex-1 items-center justify-center p-4">
                  <p className="text-sm text-muted-foreground">
                    {t("credits.upgradeDialog.noNextPlan")}
                  </p>
                </div>
              )}
            </div>
          ) : null}

          <p className="text-xs text-muted-foreground">
            {t("credits.upgradeDialog.stripeComingSoon")}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t("credits.upgradeDialog.close")}
          </Button>
          <Button asChild disabled={loading}>
            <a href={mailto}>{t("credits.upgradeDialog.contactSales")}</a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
